import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlug } from '@fortawesome/free-solid-svg-icons';
import { EhrSystemType, IdentityService, MASTER_CLINIC_ID, SessionStorage, useAuth } from '@medprompt/shared';
import '@medprompt/shared/src/common/modals.css';
import { sharedApiClient, SystmOneService } from '../fetcher';
import './SystmOneBridgeGate.css';

const POLL_INTERVAL_MS = 30000;
const BRIDGE_DOWNLOAD_URL = process.env.REACT_APP_SYSTMONE_BRIDGE_DOWNLOAD_URL;

const systmOneService = new SystmOneService(sharedApiClient);

// clinics rarely change within a session; share one fetch per user across route changes
let clinicsCache = { userId: null, promise: null };
const fetchClinics = (userId) => {
  if (clinicsCache.userId !== userId || !clinicsCache.promise) {
    const promise = new IdentityService().fetchClinics().catch((error) => {
      clinicsCache = { userId: null, promise: null };
      throw error;
    });
    clinicsCache = { userId, promise };
  }
  return clinicsCache.promise;
};

const REASON_MESSAGES = {
  NOT_REGISTERED: 'The MedPrompt Bridge has not connected yet. Open it on the computer where you use SystmOne and sign in.',
  STALE: 'The MedPrompt Bridge has stopped responding. Check it is running and connected to the internet.',
  SYSTMONE_DISCONNECTED: 'The MedPrompt Bridge cannot reach SystmOne. Make sure SystmOne is open and you are logged in.',
  WRONG_ORGANISATION: 'SystmOne is logged into a different practice. Log into this practice in SystmOne to continue.',
  OUTDATED_VERSION: 'The MedPrompt Bridge needs updating. It will update automatically, or you can reinstall it below.',
  UNREACHABLE: 'MedPrompt could not check your bridge connection. Please check your internet connection.',
};

const BridgeRequiredModal = ({ status, checking, onRetry, otherClinics }) => (
  <div className="modal-overlay systmone-bridge-overlay">
    <div className="modal-container systmone-bridge-modal modal-animated">
      <div className="modal-header">
        <h2>Connect the MedPrompt Bridge</h2>
      </div>
      <div className="modal-content">
        <div className="systmone-bridge-icon">
          <FontAwesomeIcon icon={faPlug} />
        </div>
        <p>
          This practice uses SystmOne. MedPrompt works through the MedPrompt Bridge app,
          which must be running on your computer while you use MedPrompt.
        </p>
        <p className="systmone-bridge-reason">
          {REASON_MESSAGES[status.reason] || REASON_MESSAGES.STALE}
        </p>
        {status.systmOneUserName && (
          <p className="systmone-bridge-detail">
            Last seen as <strong>{status.systmOneUserName}</strong>
            {status.bridgeVersion ? ` (version ${status.bridgeVersion})` : ''}
          </p>
        )}
        {otherClinics.length > 0 && (
          <div className="systmone-bridge-clinics">
            <span>Or switch to another practice:</span>
            {otherClinics.map((clinic) => (
              <button
                key={clinic.clinicId}
                className="cancel-button"
                onClick={() => SessionStorage.setSelectedClinicId(clinic.clinicId)}
              >
                {clinic.clinicName}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="modal-actions">
        {BRIDGE_DOWNLOAD_URL && (
          <a className="cancel-button systmone-bridge-download" href={BRIDGE_DOWNLOAD_URL}>
            Download the Bridge
          </a>
        )}
        <button className="systmone-bridge-retry" onClick={onRetry} disabled={checking}>
          {checking ? 'Checking…' : 'Check again'}
        </button>
      </div>
    </div>
  </div>
);

/**
 * Blocks the app for SystmOne practices while the user's bridge is not active for the selected clinic.
 * The bridge heartbeats every minute, so the status is re-checked periodically and the block lifts by itself.
 */
const SystmOneBridgeGate = ({ children }) => {
  const { user } = useAuth();
  const [clinics, setClinics] = useState([]);
  const [selectedClinicId, setSelectedClinicId] = useState(SessionStorage.getSelectedClinicId());
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    fetchClinics(user?.userId).then(setClinics).catch((error) => console.error('Failed to fetch clinics', error));
  }, [user?.userId]);

  useEffect(() => {
    const handleClinicChanged = () => setSelectedClinicId(SessionStorage.getSelectedClinicId());
    window.addEventListener('clinicChanged', handleClinicChanged);
    return () => window.removeEventListener('clinicChanged', handleClinicChanged);
  }, []);

  const selectedClinic = clinics.find((clinic) => clinic.clinicId === selectedClinicId);
  const isSystmOne = selectedClinic?.ehrSystem === EhrSystemType.SYSTM_ONE;

  const checkStatus = useCallback(async () => {
    setChecking(true);
    try {
      setStatus(await systmOneService.fetchBridgeStatus());
    } catch (error) {
      console.error('Failed to fetch SystmOne bridge status', error);
      setStatus({ active: false, reason: 'UNREACHABLE' });
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    setStatus(null);
    if (!isSystmOne) {
      return undefined;
    }
    checkStatus();
    const interval = setInterval(checkStatus, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isSystmOne, selectedClinicId, checkStatus]);

  const otherClinics = clinics.filter(
    (clinic) => clinic.clinicId !== selectedClinicId && clinic.clinicId !== MASTER_CLINIC_ID
  );

  return (
    <>
      {children}
      {isSystmOne && status && !status.active && (
        <BridgeRequiredModal
          status={status}
          checking={checking}
          onRetry={checkStatus}
          otherClinics={otherClinics}
        />
      )}
    </>
  );
};

export default SystmOneBridgeGate;
